<script setup lang="ts">
import type { HTMLAttributes } from 'vue';
import { nextTick, useTemplateRef } from 'vue';
import { reactiveOmit } from '@vueuse/core';
import { Check } from 'lucide-vue-next';
import type { CheckboxRootEmits, CheckboxRootProps } from 'reka-ui';
import { CheckboxIndicator, CheckboxRoot, useForwardPropsEmits } from 'reka-ui';
import { cn } from '@/lib/utils';

const props = defineProps<
    CheckboxRootProps & { class?: HTMLAttributes['class'] }
>();
const emits = defineEmits<CheckboxRootEmits>();

const delegatedProps = reactiveOmit(props, 'class');

const forwarded = useForwardPropsEmits(delegatedProps, emits);

const rootRef = useTemplateRef<InstanceType<typeof CheckboxRoot>>('rootRef');

// reka-ui dispatches `change` via a pre-flush watch, before Vue patches input.checked.
// Formie's conditions/validator reads input.checked synchronously in that handler,
// so it always sees the stale value. Re-dispatch after nextTick so Vue has updated the DOM.
function dispatchNativeChangeAfterRender() {
    nextTick(() => {
        const input = (
            rootRef.value?.$el as HTMLElement
        )?.querySelector<HTMLInputElement>('input[type="checkbox"]');
        input?.dispatchEvent(new Event('change', { bubbles: true }));
    });
}
</script>

<template>
    <CheckboxRoot
        ref="rootRef"
        v-slot="slotProps"
        data-slot="checkbox"
        v-bind="forwarded"
        @update:modelValue="dispatchNativeChangeAfterRender"
        :class="
            cn(
                'peer data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground data-[state=checked]:border-primary focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive has-[>[aria-invalid=true]]:ring-destructive/20 has-[>[aria-invalid=true]]:border-destructive size-4 shrink-0 rounded-[4px] border shadow-xs transition-shadow outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50',
                props.class
            )
        "
    >
        <CheckboxIndicator
            data-slot="checkbox-indicator"
            class="grid place-content-center text-current transition-none"
        >
            <slot v-bind="slotProps">
                <Check class="size-3.5" />
            </slot>
        </CheckboxIndicator>
    </CheckboxRoot>
</template>
